import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import axios, { type AxiosResponse } from "axios";
import { FireBaseService } from "../firebase/firebase";
import LocalStorageService from "./local-storage-service";
import { SESSION_EXPIRED_SIGN_IN_URL } from "./sign-in-reason";

declare let window: Window & { skipAlert: boolean; userInfo: unknown };

const backendUrl: string = CommonHelperFunctions.GetBackendUrl();
const localStorageService = LocalStorageService.getService();

export interface RefreshTokenResponse {
  token?: string;
  tokenString?: string;
  refreshToken?: string;
}

/**
 * The one refresh in flight, shared by every caller. The backend ROTATES refresh
 * tokens (AuthenticationProviderBase.RefreshToken tags the row it replaces), so two
 * refreshes racing on the same stored token means the loser posts a token the server
 * has already retired — it 400s, and the user is bounced to /login mid-work. Every
 * refresh path in the designer must therefore go through this module rather than
 * POSTing /auth/refreshtoken on its own.
 */
let inFlightRefresh: Promise<string> | null = null;

/** True while a refresh is on the wire — lets callers tell "refresh failed" from "no refresh was running". */
export const isRefreshInFlight = (): boolean => {
  return inFlightRefresh !== null;
};

/**
 * Refresh the access token, coalescing concurrent callers onto a single request.
 * Resolves with the new access token; rejects if the session cannot be renewed —
 * the caller decides whether that means retry, give up, or
 * {@link clearSessionAndRedirectToLogin}.
 */
export const refreshAccessToken = (): Promise<string> => {
  if (inFlightRefresh === null) {
    // Release the slot however it settles, so the next 401 starts a fresh attempt.
    // The callback is attached one link BEFORE the promise handed to callers, so the
    // slot is already clear when they resume: a caller whose retry 401s again gets a
    // genuinely new refresh rather than the settled one it just consumed.
    const refresh: Promise<string> = performRefresh().finally((): void => {
      if (inFlightRefresh === refresh) inFlightRefresh = null;
    });
    inFlightRefresh = refresh;
  }
  return inFlightRefresh;
};

/**
 * Terminal path for an unrenewable session: drop local state and bounce to login, telling the
 * sign-in page why so it can say the session expired rather than show a bare form.
 */
export const clearSessionAndRedirectToLogin = (): void => {
  localStorageService.clearToken();
  window.userInfo = undefined;
  window.skipAlert = true;
  window.location.href = SESSION_EXPIRED_SIGN_IN_URL;
};

const performRefresh = async (): Promise<string> => {
  const refreshToken: string | null = localStorageService.getRefreshToken();
  if (!refreshToken || refreshToken === "null" || refreshToken === "undefined") {
    throw new Error("No local refresh token");
  }

  const res: AxiosResponse<RefreshTokenResponse> = await axios.post<RefreshTokenResponse>(
    `${backendUrl}/auth/refreshtoken`,
    { refreshtoken: refreshToken }
  );

  if (!res || res.status !== 200) {
    throw new Error(`Refresh token endpoint returned ${res ? res.status : "no response"}`);
  }

  const accessToken: string | undefined = res.data?.tokenString ?? res.data?.token;
  if (!accessToken) {
    throw new Error("Refresh token response carried no access token");
  }

  // A server that does not rotate omits refreshToken; keeping the current one is
  // correct there and beats persisting the string "undefined".
  localStorageService.setToken({ tokenString: accessToken, refreshToken: res.data?.refreshToken ?? refreshToken });
  axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

  const firebase: FireBaseService = new FireBaseService();
  firebase.logEvent("refresh_token_called");

  return accessToken;
};
